/**
 * Add-wizard mounts for the identification dialogs (ENG-4832 / ENG-4925 / ENG-4833).
 *
 * `<IdentificationDialogs>` is mounted once by `scaffold.tsx` and owns three
 * store-facing jobs, so the panels need no identification wiring of their own:
 *
 *   1. Records what discovery identified for the selected Source row
 *      (`setDetectedCamera` + `markIdentifiedBy('detected')`), and opens C2
 *      on the brand/model step when that camera is not yet supported
 *      (`openNotListed(…, 'camera_reported')`, ENG-4924).
 *   2. Renders `<NotListedFlowConnected>` — C1/C2 opened by `openNotListed()`.
 *   3. Renders `<BasicControlsConnected>` — D1, shown when the wizard advances
 *      from the brand/model step to Connect controls and
 *      `shouldShowBasicControls` holds.
 *
 * Nothing renders or records in multi-add mode: bulk rows skip the brand/model
 * step; `runBulkCommit` reports `identified_by` from what discovery resolved
 * (see save.ts), and the multi-add "uses basic controls" list is the source
 * footer's own dialog (bulk-basic-controls-dialog.tsx, ENG-4942).
 */
import type { Collection } from '#~/api/device/registry/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { getBrands, getModels } from '#~/device/registry/commands/index.js';
import { useBrands, useModels, useModelsByBrands, useTemplate } from '#~/device/registry/hooks.js';
import { logger } from '#~/logger/index.js';
import type { Brand, Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProducer, useStore } from '../store/hooks.js';
import {
  clearTypedCamera,
  closeBasicControls,
  closeNotListed,
  markIdentifiedBy,
  notListedIdentifiedBy,
  openBasicControls,
  openNotListed,
  setDetectedCamera,
  setTypedCamera,
  shouldShowBasicControls
} from '../store/identification.slice.js';
import { Panel } from '../store/types.js';
import type { Draft } from '../store/types.js';
import { findGenericModelId, isOtherBrand } from '../video/rtsp/utils.js';
import { BasicControlsDialog, basicControlsCameraLabel } from './basic-controls-dialog.jsx';
import { NotListedFlow, typedCameraLabel } from './not-listed-flow.jsx';
import type { NotListedQuery } from './suggestions.js';
import { buildNotListedSuggestions, matchingBrandIds, toCatalogueRows } from './suggestions.js';
import { resolveDetectedCamera, resolveTypedNickname } from './utils.js';

const EMPTY_QUERY: NotListedQuery = { brand: '', model: '' };

/** The label an earlier pass of the flow may have written as the nickname. */
const previousTypedLabel = (draft: Draft): string =>
  typedCameraLabel({
    brand: draft.identification.typedBrand ?? '',
    model: draft.identification.typedModel ?? ''
  });

/** The catch-all "Other" brand: the registry's flag first, the name match as a fallback. */
const otherBrandIdOf = (brands: T_Maybe<Collection<Brand>>): T_Maybe<Brand['id']> =>
  brands?.generic?.id ?? (Object.values(brands?.items ?? {}) as Brand[]).find(isOtherBrand)?.id;

/** The "Other" brand's generic template model, same two-step lookup. */
const genericModelIdOf = (models: T_Maybe<Collection<Model>>): T_Maybe<Model['id']> =>
  models?.generic?.id ?? findGenericModelId(models);

/** C1/C2 wired to the add store and the device registry. */
export const NotListedFlowConnected = (): ReactElement | null => {
  const producer = useProducer();
  const open = useStore((µ) => !µ.source.bulkMode && (µ.identification?.notListed.open ?? false));
  const step = useStore((µ) => µ.identification?.notListed.step ?? 'c1');
  const openedFrom = useStore((µ) => µ.identification?.notListed.openedFrom ?? 'list');
  const typedBrand = useStore((µ) => µ.identification?.typedBrand);
  const typedModel = useStore((µ) => µ.identification?.typedModel);
  const { getAccessTokenSilently } = useAuth0();

  const brands = useBrands();

  // The generic "Other / Other" model Continue saves. The registry flags the
  // catch-all brand (`brands.generic`) and each brand's template model
  // (`models.generic`); the name match is the RTSP step's Brand="Other"
  // lookup, kept as a fallback. Fetched as soon as the wizard mounts, not when
  // C1 opens, so it is normally loaded before the user can reach Continue.
  const otherBrandId = useMemo(() => otherBrandIdOf(brands), [brands]);
  const otherModels = useModels(otherBrandId);
  const genericModelId = useMemo(() => genericModelIdOf(otherModels), [otherModels]);

  // Continue is never blocked on the registry (ENG-4832: both fields are
  // optional and the button is always enabled), so the rare click that lands
  // before the mount-time fetch resolved fetches the model itself. The
  // commands read the depot cache first, so the normal case costs nothing.
  const fetchGenericModelId = async (): Promise<T_Maybe<Model['id']>> => {
    const token = await getAccessTokenSilently();
    const brandId = otherBrandIdOf(await getBrands(token));

    return brandId ? genericModelIdOf(await getModels(brandId, token)) : undefined;
  };

  // Suggestions search every model already cached, and fetch the models of
  // brands strongly matching the typed brand, so typing never fans out a
  // registry request per brand.
  const [query, setQuery] = useState<NotListedQuery>(EMPTY_QUERY);
  const fetchBrandIds = useMemo(
    () => (open ? matchingBrandIds(query.brand, brands) : []),
    [open, query.brand, brands]
  );
  useModelsByBrands(fetchBrandIds);
  const cachedModels = useDepot((µ) => µ.registry?.models);
  const rows = useMemo(() => toCatalogueRows(brands, cachedModels ?? {}), [brands, cachedModels]);
  const suggestions = useCallback(
    (next: NotListedQuery) => buildNotListedSuggestions(next, rows),
    [rows]
  );

  // A fresh open searches from its own prefill, not the previous open's text.
  useEffect(() => {
    if (open) setQuery({ brand: typedBrand ?? '', model: typedModel ?? '' });
  }, [open]);

  const handleBack = (): void => {
    producer((draft) => closeNotListed(draft));
  };

  /** Continue with the generic model known: the only write path, so nothing saves without a model. */
  const finish = (typed: NotListedQuery, modelId: Model['id']): void => {
    producer((draft) => {
      const previous = previousTypedLabel(draft);

      closeNotListed(draft);
      setTypedCamera(draft, typed);
      // ENG-4832 / ENG-4924: the answer depends on which entry opened the flow
      // (`not_listed` from the list, `not_yet_supported_picked` for a
      // not-yet-supported camera, picked or recognised).
      markIdentifiedBy(draft, notListedIdentifiedBy(draft.identification.notListed.openedFrom));

      // Same model the Brand="Other" path saves, so the generic template
      // drives the connection. Stale control settings belong to the old model.
      draft.candidate.modelId = modelId;
      draft.candidate.control = undefined;

      // "<Brand> <Model>" becomes the nickname unless the user renamed it.
      const name = resolveTypedNickname({
        current: draft.candidate.name,
        derivedNames: [
          draft.source.selection?.name,
          draft.manufacturer.defaultName,
          draft.manufacturer.config?.model?.name
        ],
        previousTypedLabel: previous,
        typedLabel: typedCameraLabel(typed)
      });

      if (name) draft.candidate.name = name;
      else delete draft.candidate.name;
    });
  };

  const handleContinue = (typed: NotListedQuery): void => {
    // Normal case: the model loaded on mount, so Continue finishes synchronously.
    if (genericModelId) {
      finish(typed, genericModelId);
      return;
    }

    // Per rule: verify-and-escalate (ai.knowledge/global) — a registry failure
    // is logged (never console) and leaves the dialog open, so the user sees
    // the flow did not finish instead of a camera saved without a model.
    fetchGenericModelId()
      .then((modelId) => {
        if (modelId) finish(typed, modelId);
        else logger.error('Not-listed flow: the registry has no generic Other/Other model.');
      })
      .catch((error: unknown) => {
        logger.error('Not-listed flow: resolving the generic model failed.', error);
      });
  };

  const handleDidYouMean = (pick: { brandId: Brand['id']; modelId: Model['id'] }): void => {
    producer((draft) => {
      const previous = previousTypedLabel(draft);

      closeNotListed(draft);
      clearTypedCamera(draft);
      markIdentifiedBy(draft, 'searched');
      draft.candidate.modelId = pick.modelId;
      draft.candidate.control = undefined;

      // A nickname this flow wrote earlier no longer describes the camera.
      if (previous && draft.candidate.name === previous) delete draft.candidate.name;
    });
  };

  return (
    <NotListedFlow
      initial={{ brand: typedBrand ?? '', model: typedModel ?? '' }}
      onBack={handleBack}
      onContinue={handleContinue}
      onDidYouMean={handleDidYouMean}
      onQueryChange={setQuery}
      open={open}
      openedFrom={openedFrom}
      step={step}
      suggestions={suggestions}
    />
  );
};

/** D1 wired to the wizard's step advance. */
export const BasicControlsConnected = (): ReactElement | null => {
  const producer = useProducer();
  const panel = useStore((µ) => µ.panel);
  const open = useStore((µ) => µ.identification?.basicControlsOpen ?? false);

  // The step advance is the panel change itself: the RTSP video step and the
  // Manufacturer step both move forward to Connect controls when the user
  // leaves brand/model, so watching the transition covers both without
  // touching either footer.
  const previousPanel = useRef(panel);
  const returnPanel = useRef<Panel>(Panel.Video);

  useEffect(() => {
    const from = previousPanel.current;
    previousPanel.current = panel;

    if (panel !== Panel.Control || from >= Panel.Control) return;

    returnPanel.current = from;
    producer((draft) => {
      if (shouldShowBasicControls(draft)) openBasicControls(draft);
    });
  }, [panel]);

  // D1 names the camera (Board D: "[Brand model] uses basic controls") from
  // the resolved template, which is the model the save will carry, and the
  // registry brand it belongs to. The event reports the same two names.
  const brands = useBrands();
  const templateModel = useStore((µ) => µ.manufacturer.config?.model);
  const brandName = templateModel ? (brands?.items[templateModel.brandId]?.name ?? '') : '';
  const modelName = templateModel?.name ?? '';

  const handleClose = (): void => {
    producer((draft) => {
      closeBasicControls(draft);
      // The X returns to the brand/model step the user just left.
      draft.panel = returnPanel.current;
    });
  };

  const handleContinue = (): void => {
    producer((draft) => closeBasicControls(draft));
  };

  return (
    <BasicControlsDialog
      brandName={brandName}
      cameraLabel={basicControlsCameraLabel({ brandName, modelName })}
      modelName={modelName}
      onClose={handleClose}
      onContinue={handleContinue}
      open={open}
    />
  );
};

/**
 * Records discovery's identification for the selected Source row, and opens
 * C2 for a recognised camera the registry cannot drive yet.
 */
const useRecordDetection = (): void => {
  const producer = useProducer();
  const panel = useStore((µ) => µ.panel);
  const selection = useStore((µ) => µ.source.selection);
  const bulkMode = useStore((µ) => µ.source.bulkMode);
  const brands = useBrands();
  const selectedModelId = selection && 'modelId' in selection ? selection.modelId : undefined;
  const template = useTemplate(selectedModelId);

  // ENG-4832 / ENG-4924: discovery recognised the camera, but its registry
  // entry is "not yet supported" — tell the user on the brand/model step with
  // C2 ("<Brand model> isn't supported yet", opened_from `camera_reported`)
  // instead of letting them discover it at the control step. Once per
  // recognised model: Back leaves the detected pick in place, and returning to
  // the step must not nag again.
  const reportedModelId = useRef<T_Maybe<Model['id']>>(undefined);

  useEffect(() => {
    if (bulkMode || panel !== Panel.Manufacturer) return;

    // A template for another model (stale while the new one loads) says nothing.
    const matched = template?.model.id === selectedModelId ? template : undefined;
    if (!matched?.model.notYetSupported || reportedModelId.current === matched.model.id) return;

    reportedModelId.current = matched.model.id;
    const detected = resolveDetectedCamera(selection, matched, brands);

    producer((draft) => {
      if (!draft.identification) return;

      openNotListed(
        draft,
        'c2',
        { brand: detected?.brand, model: detected?.model },
        'camera_reported'
      );
    });
  }, [panel, template]);

  useEffect(() => {
    if (bulkMode) return;

    const detected = resolveDetectedCamera(selection, template, brands);

    producer((draft) => {
      // Only the Source step chooses the row; later steps keep their answer.
      if (draft.panel !== Panel.Source || !draft.identification) return;

      setDetectedCamera(draft, detected ?? {});
      // A new row starts a new identification: detected or not yet known.
      clearTypedCamera(draft);
      if (detected) markIdentifiedBy(draft, 'detected');
      else draft.identification.identifiedBy = undefined;
    });
  }, [selection, template, brands, bulkMode]);
};

/** `<IdentificationDialogs>` — the single wizard mount (see file header). */
export const IdentificationDialogs = (): ReactElement => {
  useRecordDetection();

  return (
    <>
      <NotListedFlowConnected />
      <BasicControlsConnected />
    </>
  );
};
